import * as React from "react";

import { getApiErrorMessage } from "api/apiError";
import type { ManagedUser } from "api/users";
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  Input,
  notify,
  Page,
  PageHeader,
} from "Components/ui";
import { Plus, Search, SearchX, Users, X } from "Components/ui/icons";
import AddUserModal from "./AddUserModal";
import RecentActivityCard from "./Components/RecentActivityCard";
import RoleMixCard from "./Components/RoleMixCard";
import { displayName, getUserId, useUsersData } from "./data";
import EditUserModal from "./EditUserModal";
import LoginHistoryModal from "./LoginHistoryModal";
import ResetPasswordModal from "./ResetPasswordModal";
import UserTable from "./UserTable";

interface UsersPageProps {
  /** Rendered inside the Settings panel, which has its own title. */
  embedded?: boolean;
}

/** SAP admin accounts: list, search, add, edit, reset password, suspend, delete, login history. */
const UsersPage: React.FC<UsersPageProps> = ({ embedded = false }) => {
  const {
    permissions,
    search,
    setSearch,
    filteredUsers,
    usersLoading,
    usersError,
    retryUsers,
    roleMix,
    activity,
    refresh,
    toggleActive,
    remove,
  } = useUsersData();
  const { canWrite, canConfigure, canViewAuditLogs } = permissions;
  const canCreate = canWrite;

  const [adding, setAdding] = React.useState(false);
  const [editing, setEditing] = React.useState<ManagedUser | null>(null);
  const [resetting, setResetting] = React.useState<ManagedUser | null>(null);
  const [historyFor, setHistoryFor] = React.useState<ManagedUser | null>(null);
  const [suspending, setSuspending] = React.useState<ManagedUser | null>(null);
  const [deleting, setDeleting] = React.useState<ManagedUser | null>(null);

  const handleEdit = React.useCallback(
    (user: ManagedUser) => {
      if (!canWrite) {
        notify.error("You don't have permission to edit users.");
        return;
      }
      setEditing(user);
    },
    [canWrite]
  );

  const handleResetPassword = React.useCallback(
    (user: ManagedUser) => {
      if (!canConfigure) {
        notify.error("You don't have permission to reset passwords.");
        return;
      }
      if (!getUserId(user)) {
        notify.error("This user has no id, so their password can't be reset.");
        return;
      }
      setResetting(user);
    },
    [canConfigure]
  );

  // Suspending asks first; activating is safe to do straight away.
  const handleToggleActive = React.useCallback(
    (user: ManagedUser) => {
      if (user.isActive) {
        setSuspending(user);
        return;
      }
      // toggleActive already reported the failure.
      toggleActive(user).catch(() => undefined);
    },
    [toggleActive]
  );

  const handleDelete = React.useCallback((user: ManagedUser) => setDeleting(user), []);
  const handleLoginHistory = React.useCallback((user: ManagedUser) => setHistoryFor(user), []);

  const addButton = canCreate ? (
    <Button leftIcon={<Plus />} onClick={() => setAdding(true)}>
      Add user
    </Button>
  ) : null;

  const searchInput = (
    <Input
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      placeholder="Search name, username, email, role or number"
      aria-label="Search users"
      leading={<Search />}
      trailing={
        search ? (
          <IconButton
            label="Clear search"
            icon={<X />}
            size="sm"
            hideTooltip
            onClick={() => setSearch("")}
          />
        ) : undefined
      }
      wrapperClassName="w-full sm:max-w-sm"
    />
  );

  const query = search.trim();
  const empty = query ? (
    <EmptyState
      compact
      icon={<SearchX weight="duotone" />}
      title="No matching users"
      description={`Nothing matches “${query}”. Try a name, email or role.`}
      action={
        <Button variant="secondary" size="sm" onClick={() => setSearch("")}>
          Clear search
        </Button>
      }
    />
  ) : (
    <EmptyState
      compact
      icon={<Users weight="duotone" />}
      title="No users yet"
      description="Add the people who should be able to sign in to SAP."
      action={addButton}
    />
  );

  const list = usersError ? (
    <Card>
      <ErrorState
        title="Users didn't load"
        description={getApiErrorMessage(usersError)}
        action={
          <Button variant="secondary" onClick={retryUsers}>
            Try again
          </Button>
        }
      />
    </Card>
  ) : (
    <UserTable
      users={filteredUsers}
      loading={usersLoading}
      permissions={permissions}
      searchQuery={search}
      empty={empty}
      onEdit={handleEdit}
      onResetPassword={handleResetPassword}
      onLoginHistory={handleLoginHistory}
      onToggleActive={handleToggleActive}
      onDelete={handleDelete}
    />
  );

  const content = (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="min-w-0">{list}</div>
      <aside className="space-y-6" aria-label="Overview">
        <RoleMixCard mix={roleMix} loading={usersLoading} />
        <RecentActivityCard
          canView={canViewAuditLogs}
          loading={activity.loading}
          failed={activity.failed}
          entries={activity.entries}
        />
      </aside>
    </div>
  );

  const overlays = (
    <>
      <AddUserModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false);
          refresh();
        }}
      />
      <EditUserModal
        open={Boolean(editing)}
        user={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />
      <ResetPasswordModal
        open={Boolean(resetting)}
        userId={resetting ? getUserId(resetting) : null}
        userName={resetting ? displayName(resetting) : undefined}
        onClose={() => setResetting(null)}
        onSaved={() => {
          setResetting(null);
          refresh();
        }}
      />
      <LoginHistoryModal
        open={Boolean(historyFor)}
        userId={historyFor ? getUserId(historyFor) : null}
        userName={historyFor ? displayName(historyFor) : undefined}
        onClose={() => setHistoryFor(null)}
      />
      <ConfirmDialog
        open={Boolean(suspending)}
        onOpenChange={(next) => (next ? undefined : setSuspending(null))}
        tone="danger"
        title={suspending ? `Suspend ${displayName(suspending)}?` : "Suspend user?"}
        description="They lose access to SAP and can't sign in until you activate the account again."
        confirmLabel="Suspend"
        onConfirm={async () => {
          if (suspending) {
            await toggleActive(suspending);
          }
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(next) => (next ? undefined : setDeleting(null))}
        tone="danger"
        title={deleting ? `Delete ${displayName(deleting)}?` : "Delete user?"}
        description="The account is removed for good and can't be restored. Past audit log entries stay."
        confirmLabel="Delete user"
        confirmText={deleting ? deleting.userName || deleting.name : undefined}
        onConfirm={async () => {
          if (deleting) {
            await remove(deleting);
          }
        }}
      />
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          {searchInput}
          {addButton}
        </div>
        {content}
        {overlays}
      </div>
    );
  }

  return (
    <Page width="wide">
      <PageHeader
        title="Users"
        description="People who can sign in to SAP and the role each one has."
        actions={addButton}
      >
        {searchInput}
      </PageHeader>
      {content}
      {overlays}
    </Page>
  );
};

export default UsersPage;
